import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createBlock as _createBlock, vModelText as _vModelText, unref as _unref, withModifiers as _withModifiers, withKeys as _withKeys, createElementVNode as _createElementVNode, withDirectives as _withDirectives, createVNode as _createVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["placeholder", "onKeydown"]
const _hoisted_2 = { class: "activity-form-panel-contact-input__dropdown-item-text" }

import { ref, computed, inject, nextTick, useTemplateRef } from 'vue';
import { getFullNameUser } from 'Helpers/avatarHelper';
import { debounce, capitalize } from 'lodash';
import { hasWhiteSpace } from 'Helpers/stringHelper';
import { validateEmail } from 'Helpers/validator/EmailValidator';
import { Translator } from 'types/injects/translator.type';

interface Contact {
    id: string | number;
    type: 'contact' | 'external';
}

interface ContactInfo {
    id: string | number;
    first_name: string;
    last_name: string;
    email: string;
}

interface Props {
    contacts: Contact[];
    dataFromAutocomplete: ContactInfo[];
    isDropdownLoading?: boolean;
    embedParticipants?: ContactInfo[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ActivityFormPanelContactInput',
  props: {
    contacts: {},
    dataFromAutocomplete: {},
    isDropdownLoading: { type: Boolean, default: false },
    embedParticipants: { default: () => [] }
  },
  emits: ["update:contacts", "input"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const $t = inject('$t') as Translator;

const translations: Record<string, string> = {
    searchContacts: $t.__('Ajouter des invités'),
    noResults: $t.__('Aucun résultat trouvé'),
};

const inputWrapperRef = ref<HTMLDivElement>();
const inputRef = ref<HTMLInputElement>();
const dropdownRef = useTemplateRef('dropdownRef');

const input = ref('');
const isFocused = ref(false);
const isDropdownVisible = ref(false);
const emailAddresses = ref<Contact[]>([...props.contacts]);
const contactsInformation = ref<ContactInfo[]>(
    props.contacts
        .filter((participant) => participant.type === 'contact')
        .map((participant) => {
            return (
                props.dataFromAutocomplete.find((contact) => contact.id === participant.id) ??
                props.embedParticipants.find((contact) => contact.id === participant.id)
            );
        })
        .filter((info): info is ContactInfo => !!info),
);

const selectedContactIds = computed((): (string | number)[] => {
    return emailAddresses.value
        .filter((contact) => contact.type === 'contact')
        .map((contact) => contact.id);
});

const tagValues = computed((): Contact[] => {
    return emailAddresses.value.filter(
        (participant) => !participant.id.toString().includes('@resource.calendar.google.com'),
    );
});

const updatePopper = (): void => {
    dropdownRef.value?.updatePopper();
};

const getTagColor = (value: Contact): string => {
    if (value.type === 'external') {
        return validateEmail(value.id.toString()) ? 'blue' : 'red';
    }
    return 'blue';
};

const isEmailAdded = (email: string | number): boolean => {
    return emailAddresses.value.some((item) => item.id === email);
};

const handleRemoveTag = (emailAddress: string | number): void => {
    emailAddresses.value = emailAddresses.value.filter((email) => email.id !== emailAddress);
    emit('update:contacts', emailAddresses.value);
    contactsInformation.value = contactsInformation.value.filter(
        (contact) => contact.id !== emailAddress,
    );
    nextTick(() => updatePopper());
};

const addEmailAddress = (contact: ContactInfo | null = null): void => {
    const id = contact?.id ?? input.value;

    if (emailAddresses.value.some((email) => email.id === id)) {
        handleRemoveTag(id);
        return;
    }

    emailAddresses.value.push({
        id,
        type: contact ? 'contact' : 'external',
    });

    if (contact) {
        contactsInformation.value.push(
            props.dataFromAutocomplete.find((item) => item.id === contact.id) as ContactInfo,
        );
    }

    emit('update:contacts', emailAddresses.value);
    input.value = '';
    nextTick(() => updatePopper());
};

const onItemSelected = (value: string | number): void => {
    const contact = props.dataFromAutocomplete.find((item) => item.id === value);

    if (contact && !isEmailAdded(contact.email)) {
        addEmailAddress(contact);
    } else if (contact) {
        handleRemoveTag(contact.email);
    }
    nextTick(() => {
        isDropdownVisible.value = true;
        inputRef.value?.focus();
        updatePopper();
    });
};

const onInput = debounce((): void => {
    emit('input', input.value);
}, 500);

const handleEmail = (): void => {
    if (input.value.trim().length && !hasWhiteSpace(input.value)) {
        addEmailAddress();
    }
};

const handleFocus = (): void => {
    isFocused.value = true;
    nextTick(() => {
        inputRef.value?.focus();
    });
    updatePopper();
    isDropdownVisible.value = true;
};

const handleBlur = (): void => {
    isFocused.value = false;
    isDropdownVisible.value = false;
    input.value = '';
};

const findParticipant = (email: string | number): string => {
    const participant = contactsInformation.value.find((contact) => contact.id === email);
    if (participant) {
        return `${capitalize(participant.first_name)} ${capitalize(participant.last_name)}`;
    }
    return email.toString();
};

const getInitials = (person: ContactInfo): string => {
    const initialFirstName = person.first_name ? person.first_name[0] : '';
    const initialLastName = person.last_name ? person.last_name[0] : '';
    return `${initialFirstName}${initialLastName}`;
};

return (_ctx: any,_cache: any) => {
  const _component_CompassTagInteractive = _resolveComponent("CompassTagInteractive")!
  const _component_CompassAvatar = _resolveComponent("CompassAvatar")!
  const _component_CompassDropdownOption = _resolveComponent("CompassDropdownOption")!
  const _component_CompassDropdown = _resolveComponent("CompassDropdown")!

  return (_openBlock(), _createElementBlock("div", {
    ref_key: "inputWrapperRef",
    ref: inputWrapperRef,
    class: _normalizeClass(["activity-form-panel-contact-input", { 'activity-form-panel-contact-input--focused': isFocused.value }]),
    onClick: handleFocus
  }, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tagValues.value, (emailAddress) => {
      return (_openBlock(), _createBlock(_component_CompassTagInteractive, {
        key: emailAddress.id,
        size: "small",
        color: getTagColor(emailAddress),
        modelValue: emailAddress.id,
        valueLabel: findParticipant(emailAddress.id),
        class: "activity-form-panel-contact-input__tag",
        onTagRemove: ($event: any) => (handleRemoveTag(emailAddress.id))
      }, null, 8 /* PROPS */, ["color", "modelValue", "valueLabel", "onTagRemove"]))
    }), 128 /* KEYED_FRAGMENT */)),
    _withDirectives(_createElementVNode("input", {
      ref_key: "inputRef",
      ref: inputRef,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((input).value = $event)),
      maxlength: "320",
      placeholder: tagValues.value.length === 0 ? translations.searchContacts : '',
      autocomplete: "off",
      class: "activity-form-panel-contact-input__input typographies-paragraph-medium-regular",
      onInput: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(onInput) && _unref(onInput)(...args))),
      onKeydown: [
        _withKeys(_withModifiers(handleEmail, ["prevent"]), [","]),
        _withKeys(_withModifiers(handleEmail, ["prevent"]), ["tab"]),
        _withKeys(_withModifiers(handleEmail, ["prevent"]), ["enter"])
      ],
      onFocus: handleFocus,
      onBlur: handleBlur
    }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_1), [
      [_vModelText, input.value]
    ]),
    _createVNode(_component_CompassDropdown, {
      ref_key: "dropdownRef",
      ref: dropdownRef,
      isVisible: isDropdownVisible.value,
      "onUpdate:isVisible": _cache[2] || (_cache[2] = ($event: any) => ((isDropdownVisible).value = $event)),
      noResultsLabel: translations.noResults,
      modelValue: selectedContactIds.value,
      noResults: __props.dataFromAutocomplete.length === 0,
      parentRef: "inputWrapperRef",
      loading: __props.isDropdownLoading,
      dropdownClass: "activity-form-panel-contact-input__dropdown",
      onMousedown: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["prevent"]))
    }, {
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.dataFromAutocomplete, (item) => {
          return (_openBlock(), _createBlock(_component_CompassDropdownOption, {
            key: item.id,
            label: _unref(getFullNameUser)(item),
            value: item.id,
            onItemSelected: ($event: any) => (onItemSelected(item.id))
          }, {
            default: _withCtx(() => [
              _createVNode(_component_CompassAvatar, {
                initials: getInitials(item),
                size: "extra-small"
              }, null, 8 /* PROPS */, ["initials"]),
              _createElementVNode("span", _hoisted_2, _toDisplayString(_unref(getFullNameUser)(item)), 1 /* TEXT */)
            ]),
            _: 2 /* DYNAMIC */
          }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["label", "value", "onItemSelected"]))
        }), 128 /* KEYED_FRAGMENT */))
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["isVisible", "noResultsLabel", "modelValue", "noResults", "loading"])
  ], 2 /* CLASS */))
}
}

})